import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, CreditCard, Tag, Gift } from 'lucide-react';
import TaskToggleCard from './TaskToggleCard';
import type { CommitmentTaskType, RewardMethodType } from '@clareefai/types';

interface TaskSelection {
  type: CommitmentTaskType;
  requiredCount: number;
}

interface ConfigureRewardStepProps {
  email: string;
  rewardMethod: RewardMethodType;
  giftCardAmount: number;
  giftCardCurrency: string;
  discountUnit: string;
  discountAmount: number;
  additionalNote: string;
  selectedTasks: TaskSelection[];
  deadline: string;
  onRewardMethodChange: (method: RewardMethodType) => void;
  onGiftCardAmountChange: (amount: number) => void;
  onGiftCardCurrencyChange: (currency: string) => void;
  onDiscountUnitChange: (unit: string) => void;
  onDiscountAmountChange: (amount: number) => void;
  onAdditionalNoteChange: (note: string) => void;
  onTaskToggle: (type: CommitmentTaskType) => void;
  onTaskCountChange: (type: CommitmentTaskType, count: number) => void;
  onDeadlineChange: (date: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
}

const TASK_TYPES: CommitmentTaskType[] = ['REVIEW', 'REFERENCE_CALL', 'VIDEO', 'USE_CASE'];

const REWARD_METHODS: { value: RewardMethodType; labelKey: string; icon: typeof CreditCard }[] = [
  { value: 'GIFT_CARD', labelKey: 'rewards.specific.configure.methodGiftCard', icon: CreditCard },
  { value: 'DISCOUNT', labelKey: 'rewards.specific.configure.methodDiscount', icon: Tag },
  { value: 'BOTH', labelKey: 'rewards.specific.configure.methodBoth', icon: Gift },
];

const ConfigureRewardStep: React.FC<ConfigureRewardStepProps> = ({
  email, rewardMethod, giftCardAmount, giftCardCurrency, discountUnit, discountAmount,
  additionalNote, selectedTasks, deadline,
  onRewardMethodChange, onGiftCardAmountChange, onGiftCardCurrencyChange,
  onDiscountUnitChange, onDiscountAmountChange, onAdditionalNoteChange,
  onTaskToggle, onTaskCountChange, onDeadlineChange,
  onBack, onCancel, onNext,
}) => {
  const { t } = useTranslation();
  const showGiftCard = rewardMethod === 'GIFT_CARD' || rewardMethod === 'BOTH';
  const showDiscount = rewardMethod === 'DISCOUNT' || rewardMethod === 'BOTH';

  const hasRewardValue =
    (showGiftCard && giftCardAmount > 0) ||
    (showDiscount && discountAmount > 0);

  const isValid = selectedTasks.length > 0 && hasRewardValue && deadline;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('rewards.specific.configure.title')}</h3>
        <p className="text-gray-500">{t('rewards.specific.configure.subtitle')}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Contact email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.configure.contactEmail')}</label>
          <input type="email" value={email} readOnly className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" />
        </div>

        {/* Reward Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('rewards.specific.configure.rewardType')}</label>
          <div className="grid grid-cols-3 gap-3">
            {REWARD_METHODS.map(({ value, labelKey, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => onRewardMethodChange(value)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  rewardMethod === value
                    ? 'border-[#4339A0] bg-[#4339A0]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-1.5 ${
                  rewardMethod === value ? 'text-[#4339A0]' : 'text-gray-400'
                }`} />
                <div className={`text-sm font-medium ${
                  rewardMethod === value ? 'text-[#4339A0]' : 'text-gray-700'
                }`}>{t(labelKey)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Gift Card Amount */}
        {showGiftCard && (
          <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4 space-y-3">
            <label className="block text-sm font-semibold text-emerald-800">{t('rewards.specific.configure.giftCard')}</label>
            <div className="flex gap-3">
              <select
                value={giftCardCurrency}
                onChange={(e) => onGiftCardCurrencyChange(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent bg-white"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
              <input
                type="number"
                value={giftCardAmount || ''}
                onChange={(e) => onGiftCardAmountChange(parseInt(e.target.value) || 0)}
                placeholder={t('rewards.specific.configure.amount')}
                min="0"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Discount */}
        {showDiscount && (
          <div className="bg-orange-50/50 border border-orange-200 rounded-xl p-4 space-y-3">
            <label className="block text-sm font-semibold text-orange-800">{t('rewards.specific.configure.discount')}</label>
            <div className="flex gap-3">
              <select
                value={discountUnit}
                onChange={(e) => onDiscountUnitChange(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent bg-white"
              >
                <option value="percentage">{t('rewards.specific.configure.unitPercentage')}</option>
                <option value="credits">{t('rewards.specific.configure.unitCredits')}</option>
                <option value="free_months">{t('rewards.specific.configure.unitFreeMonths')}</option>
              </select>
              <input
                type="number"
                value={discountAmount || ''}
                onChange={(e) => onDiscountAmountChange(parseFloat(e.target.value) || 0)}
                placeholder={t('rewards.specific.configure.amount')}
                min="0"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
              />
            </div>
          </div>
        )}

        {rewardMethod === 'BOTH' && (
          <p className="text-xs text-gray-500 text-center -mt-2">{t('rewards.specific.configure.bothHint')}</p>
        )}

        {/* Additional Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.configure.additionalNote')} <span className="text-gray-400">{t('rewards.specific.configure.optional')}</span></label>
          <textarea
            value={additionalNote}
            onChange={(e) => onAdditionalNoteChange(e.target.value)}
            rows={2}
            placeholder={t('rewards.specific.configure.additionalNotePlaceholder')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
          />
        </div>

        {/* Action Required */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">{t('rewards.specific.configure.actionRequired')}</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TASK_TYPES.map((type) => {
              const selected = selectedTasks.find((t) => t.type === type);
              return (
                <TaskToggleCard
                  key={type}
                  type={type}
                  isSelected={!!selected}
                  requiredCount={selected?.requiredCount ?? 1}
                  onToggle={() => onTaskToggle(type)}
                  onCountChange={(count) => onTaskCountChange(type, count)}
                />
              );
            })}
          </div>
        </div>

        {/* Deadline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.configure.completionDate')}</label>
          <input
            type="date"
            value={deadline}
            onChange={(e) => onDeadlineChange(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="flex items-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back')}
        </button>
        <div className="flex gap-3">
          <button onClick={onCancel} className="px-6 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
            {t('common.cancel')}
          </button>
          <button
            onClick={onNext}
            disabled={!isValid}
            className="px-6 py-2.5 text-sm font-medium text-white bg-[#4339A0] hover:bg-[#3d2f8f] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('rewards.specific.configure.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigureRewardStep;
